import { useEffect } from "react";
import useWebsiteSettings from "@/hooks/useWebsiteSettings";

/**
 * Applies the favicon uploaded in /admin/settings.
 *
 * The app is client-rendered, so index.html ships a static default icon and
 * this swaps in the operator's upload once settings load. Without it the
 * faviconUrl field would save to Firestore and never reach a browser tab —
 * which is exactly what it did before this component existed.
 *
 * Both `icon` and `apple-touch-icon` are set so iOS home-screen bookmarks match
 * the browser tab.
 */

const MANAGED = "data-cs-favicon";

function applyIcon(rel, href) {
    // Drop any previous managed tag for this rel before adding the new one, so
    // repeated setting changes cannot pile up <link> elements.
    document.head.querySelectorAll(`link[${MANAGED}="${rel}"]`).forEach((el) => el.remove());

    // The static default must be REMOVED, not disabled. `disabled` only has
    // meaning on stylesheet links — on an icon link it is inert, so the default
    // stayed in the running and Chrome kept choosing it over the upload
    // (it favours the scalable SVG when several icons are offered).
    document.head
        .querySelectorAll(`link[rel="${rel}"]:not([${MANAGED}]), link[rel="shortcut icon"]:not([${MANAGED}])`)
        .forEach((el) => el.remove());

    const link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    link.setAttribute(MANAGED, rel);
    document.head.appendChild(link);
}

export default function SiteFavicon() {
    const settings = useWebsiteSettings();
    const faviconUrl = settings?.faviconUrl;

    useEffect(() => {
        if (!faviconUrl) return undefined;

        applyIcon("icon", faviconUrl);
        applyIcon("apple-touch-icon", faviconUrl);

        return () => {
            // Only the managed tags are cleaned up. The static default was
            // removed outright above and is not restored — this component lives
            // for the app's lifetime, so the only re-run is a settings change,
            // which immediately installs a replacement.
            document.head.querySelectorAll(`link[${MANAGED}]`).forEach((el) => el.remove());
        };
    }, [faviconUrl]);

    return null;
}
